import { ReactNode, createContext, useContext } from "react";
import { useParams } from "react-router-dom";
import { Packaging, useGetOnePackaging } from "src/api/client-api/packagings/useGetOnePackaging";
import { useGetPackagingPost } from "src/api/client-api/post/useGetPackagingPost";
import { useGetOneSsq } from "src/api/sure-api/ssq/useGetOneSsq";
import { Post } from "src/api/client-api/post/postSchema";
import { Ssq } from "src/api/sure-api/ssq/useGetOneSsq";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import {
    EntityBasedScoreArgs,
    useEntityBasedScoreParams,
} from "src/context/EntityBasedScore/useEntityBasedScoreParams";
import { ScoreService, ScoreVariation } from "src/context/EntityBasedScore/ScoreService";
import { Score } from "src/types";

export const DIMENSION = "headings";

type PackagingContextInitialValues = {
    packaging: Packaging | undefined;
    isLoading: boolean;
    isRefetching: boolean;

    ssq: Ssq | undefined;
    post: Post | undefined;
    isSsqLoading: boolean;
    isPostLoading: boolean;
    fetchPost: () => void;

    allScoreWithVariations: ScoreVariation[];
    scoreState: EntityBasedScoreArgs;
    packagingScores: Score[];
    selectedScore: ScoreVariation;
};
const PackagingContext = createContext<PackagingContextInitialValues | null>(null);

type PackagingContextProviderProps = { children: ReactNode };
export const PackagingContextProvider = (props: PackagingContextProviderProps) => {
    const { children } = props;
    const { packagingId } = useParams();
    const { getScoresByType } = useFoodpilotContext();

    const entityParams = useEntityBasedScoreParams({
        initDimensions: {
            dimensions: [[DIMENSION]],
        },
    });

    const { packaging, isLoading, isRefetching } = useGetOnePackaging({
        packagingId: packagingId,
        scoreQuery: entityParams.query,
    });

    const ssqId = packaging?.ssqId;
    const { ssq, isLoading: isSsqLoading } = useGetOneSsq(ssqId);
    const { post, isLoading: isPostLoading, refetch: fetchPost } = useGetPackagingPost(packaging?.id);

    const packagingScores = getScoresByType("packaging");

    const allScoreWithVariations = ScoreService.getScoreList(
        packagingScores,
        packaging?.scores,
        (scoreId, scoreVariationId) => {
            entityParams.state.selectVariation(scoreId, scoreVariationId);
        },
        entityParams.state.lastSelectedVariation,
    );

    const items: PackagingContextInitialValues = {
        packaging,
        isLoading,
        isRefetching,
        ssq,
        isSsqLoading,
        post,
        isPostLoading,
        fetchPost: fetchPost,
        allScoreWithVariations: allScoreWithVariations.scoresWithVariations,
        selectedScore: allScoreWithVariations.lastSelected,
        packagingScores,
        scoreState: entityParams,
    };

    return <PackagingContext.Provider value={items}>{children}</PackagingContext.Provider>;
};

export const usePackagingContext = () => {
    const context = useContext(PackagingContext);

    if (context) {
        return context;
    }

    throw new Error(`usePackagingContext must be used within an PackagingContextProvider`);
};
